/*
 * game-options.ts — the rule variants a host can turn on for a table.
 *
 * These ride on GameTable, which is persisted as one `state_json` blob, so
 * adding an option needs no migration: tables saved before it existed simply
 * have no value for it and the repo fills in the default on read.
 *
 * Every option defaults to the rules the game has always played by, so an
 * untouched table behaves exactly as before.
 */

export interface GameOptions {
  /**
   * Non-bidding teams keep the set points they win. Off by default, where only
   * the bidding team's score moves and everyone else plays purely to set them.
   */
  nonBiddingTeamsScore: boolean;
  /**
   * Who is stuck with the minimum bid when every player passes. On, it's the
   * dealer — the last player to bid, which is the usual house rule. Off (the
   * default) it's the player who opened the bidding.
   */
  stickTheDealer: boolean;
}

export const DEFAULT_GAME_OPTIONS: GameOptions = {
  nonBiddingTeamsScore: false,
  stickTheDealer: false,
};

/*
 * Copy for each rule, shared by the create-game form and the in-game info panel
 * so the description a host reads when turning a rule on is the same one every
 * player sees at the table.
 */
export const GAME_OPTION_COPY: Record<keyof GameOptions, { label: string; detail: string }> = {
  nonBiddingTeamsScore: {
    label: 'Non-bidding teams score',
    detail: 'Every team banks the set points it takes, not just the team that won the bid.',
  },
  stickTheDealer: {
    label: 'Stick the dealer',
    detail: 'If everyone passes, the dealer takes the minimum bid instead of the opening bidder.',
  },
};

/** The rules turned on for a table, i.e. everything differing from the defaults. */
export const activeGameOptions = (options: GameOptions): (keyof GameOptions)[] =>
  (Object.keys(GAME_OPTION_COPY) as (keyof GameOptions)[]).filter(
    (key) => options[key] !== DEFAULT_GAME_OPTIONS[key],
  );
